import React from 'react';
import Chart from 'react-apexcharts';
import { DateTime } from 'luxon';
import { Typography, Box } from '@mui/material';
import ToggleViewButton from './ToggleViewButton';

const WeeklyPeakUsageChart = ({ weeklyPeakUsages, timeZone, isMonthly, handleToggle }) => {
  // Extract day names from dates (e.g., "Mon", "Tue") for x-axis categories
  const dayNames = weeklyPeakUsages.map(day =>
    DateTime.fromISO(day.date).toFormat('ccc')
  );

  // Extract peakInUse values for the bar chart series
  const peakInUseData = weeklyPeakUsages.map(day => day.peakInUse);

  // Chart options, inspired by the original bar chart configuration
  const options = {
    chart: {
      type: 'bar',
      height: 200,
      toolbar: {
        show: false, // Disable toolbar for simplicity; can be enabled if needed
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadius: 5,
      },
    },
    dataLabels: {
      enabled: false, // No data labels on bars, consistent with original
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories: dayNames, // Mon, Tue, Wed, etc.
      labels: {
        style: {
          fontSize: '12px',
          color: '#4A5568',
        },
      },
    },
    yaxis: {
      title: {
        text: 'Quantity',
        style: {
          fontSize: '10px',
          color: '#4A5568',
        },
      },
      labels: {
        style: {
          fontSize: '12px',
          color: '#4A5568',
        },
      },
    },
    fill: {
      opacity: 1,
    },
    colors: ['#89D8C2'], // Single color for peakInUse bars; matches "In use" from original
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex }) {
        const dayData = weeklyPeakUsages[dataPointIndex];
        const dayName = dayNames[dataPointIndex];
        const peakInUse = dayData.peakInUse;
        // Format peak periods in the specified time zone
        const peakPeriods = dayData.peakPeriods.map(period => {
          const start = DateTime.fromISO(period.peakStart, { zone: 'utc' })
            .setZone(timeZone)
            .toFormat('hh:mm a');
          const end = DateTime.fromISO(period.peakEnd, { zone: 'utc' })
            .setZone(timeZone)
            .toFormat('hh:mm a');
          return `${start} - ${end}`;
        });

        // Create HTML for peak periods, each on a new line
        const peakPeriodsHtml = peakPeriods.length > 0
          ? peakPeriods.map(period => `<div>${period}</div>`).join('')
          : '<div>None</div>';

        // Format the date in US format (MM/DD/YYYY)
        const formattedDate = dayData?.date
          ? DateTime.fromISO(dayData.date).toFormat('MM/dd/yyyy')
          : 'N/A';

        // Return HTML string for tooltip
        return `
          <div style="padding: 10px; background: #2D3748; color: #fff; border-radius: 8px; font-size: 12px;">
            <strong>${dayName} ${formattedDate}</strong><br/>
            Peak Usage: ${peakInUse}<br/>
            Peak Times:<br/>
            ${peakPeriodsHtml}
          </div>
        `;
      },
    },
    legend: {
      show: false, // Only one series, so no legend needed
    },
  };

  // Series data: single series for peakInUse
  const series = [
    {
      name: 'Peak In Use',
      data: peakInUseData,
    },
  ];

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: 600,
          color: '#1B3665',
          mb: 0,
        }}
      >
        Last seven days
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <ToggleViewButton isMonthly={isMonthly} onToggle={handleToggle} />
      </Box>
      </Box>
      <Chart
        options={options}
        series={series}
        type="bar"
        height={150}
        width={260}
      />
    </>
  );
};

export default WeeklyPeakUsageChart;